import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
  ViewChild,
  AfterContentInit,
  HostListener,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LaftStepperComponent } from 'src/app/shared/UI/organisms/laft-stepper/laft-stepper/laft-stepper.component';
import { LaftService } from 'src/app/shared/services/laft.service';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';

@Component({
  selector: 'app-laft-modal',
  templateUrl: './laft-modal.component.html',
  styleUrls: ['./laft-modal.component.scss'],
})
export class LaftModalComponent implements OnInit, OnDestroy, AfterContentInit {
  @Output() formSubmitted = new EventEmitter<any>();
  @Output() saveEvent = new EventEmitter<any>();
  @ViewChild(LaftStepperComponent) stepper!: LaftStepperComponent;

  public currentStep = 1;
  private unsubscribe$ = new Subject<void>();
  private formData: any;
  private formInProgress = false;

  private readonly ID_COPIES_STEP = 5;

  /** With the flag off the form ends on investigaciones; with it on, on the ID copies. */
  public readonly isEarlyDepositEnabled: boolean;

  constructor(
    public dialogRef: MatDialogRef<LaftModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router,
    private route: ActivatedRoute,
    private laftService: LaftService,
    private featureFlagService: FeatureFlagService
  ) {
    if (this.data?.LAFTData) {
      this.formData = this.data?.LAFTData;
      this.formInProgress = true;
    }

    // Read once, not per change detection: the stepper counts its projected steps
    // in ngAfterContentInit, so a flag that flipped mid-dialogue would leave the
    // rendered steps and totalSteps disagreeing.
    this.isEarlyDepositEnabled = this.featureFlagService.isEarlyDepositEnabled();
  }

  get totalSteps(): number {
    return this.isEarlyDepositEnabled ? this.ID_COPIES_STEP : this.ID_COPIES_STEP - 1;
  }

  /** Declared on step 1; step 5 shows it back rather than asking again. */
  get declaredIdType(): string {
    return this.formData?.idType || this.data?.LAFTData?.idType || this.data?.user?.idType || '';
  }

  get declaredIdNumber(): string {
    return this.formData?.idNumber || this.data?.LAFTData?.idNumber || this.data?.user?.idNumber || '';
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      const laftStep = parseInt(params['laft_step']);
      if (laftStep && laftStep >= 1 && laftStep <= this.totalSteps) {
        this.updateCurrentStep(laftStep);
        this.formInProgress = true;
      }
    });

    if (localStorage.getItem('cleanUrlOnReload') === 'true' && this.isPageReload()) {
      setTimeout(() => {
        localStorage.removeItem('cleanUrlOnReload');
      }, 500);
    }
  }

  private isPageReload(): boolean {
    try {
      const entries = performance.getEntriesByType('navigation');
      if (entries && entries.length > 0) {
        return (entries[0] as any).type === 'reload';
      }
      if (performance.navigation) {
        return performance.navigation.type === performance.navigation.TYPE_RELOAD;
      }
    } catch {
      return false;
    }
    return false;
  }

  ngAfterContentInit(): void {
    this.updateUrlStep(1);
    if (this.data?.LAFTData?.step) {
      this.updateUrlStep(parseInt(this.data?.LAFTData?.step));
      this.formInProgress = true;
    }
  }

  ngOnDestroy(): void {
    this.removeUrlStep();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.formInProgress) {
      event.preventDefault();
      event.returnValue = '';
      localStorage.setItem('cleanUrlOnReload', 'true');
    }
  }

  private updateCurrentStep(step: number): void {
    if (this.currentStep !== step) {
      this.currentStep = step;
      this.updateStepperStep(step);
    }
  }

  private updateStepperStep(step: number): void {
    if (this.stepper && step >= 1 && step <= this.totalSteps) {
      this.stepper.currentStepIndex = step;
      this.stepper.navigation();
    }
  }

  nextStep(stepNumber: number): void {
    if (this.stepper) {
      this.stepper.next({ stepNumber });
    }

    const nextStepNumber = stepNumber + 1;
    this.currentStep = nextStepNumber;
    this.updateUrlStep(nextStepNumber);
  }

  previousStep(): void {
    if (this.stepper) {
      this.stepper.previous();
    }

    if (this.currentStep > 1) {
      this.currentStep--;
      this.updateUrlStep(this.currentStep);
      // this.laftService.logLaftDeclarationAction(
      //   'Previous step LAFT (persona natural)',
      //   'Previous step LAFT (persona natural)',
      //   this.data?.LAFTData?._id
      // );
    }
  }

  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps && step !== this.currentStep) {
      this.currentStep = step;
      this.updateStepperStep(step);
      this.updateUrlStep(step);
    }
  }

  onStepperSubmit(formData: any): void {
    const payload = this.buildPayload();
    this.formSubmitted.emit(payload);
    this.close();
  }

  private updateUrlStep(step: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { laft_step: step },
      queryParamsHandling: 'merge',
    });
  }

  private removeUrlStep(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { laft_step: null },
      replaceUrl: true,
    });
  }

  onFormNextStep(nextStepNumber): void {
    this.currentStep = nextStepNumber;
    if (this.formData) {
      this.formData.step = nextStepNumber.toString();
    }
    this.save(false);
    this.updateUrlStep(nextStepNumber);
  }

  /**
   * Investigaciones is the last step only while the flag is off, and the stepper
   * turns the last step's next into a submit. Advancing past it is therefore
   * conditional, where steps 1-3 can bind their successor directly.
   */
  onInvestigationNextStep(): void {
    if (this.isEarlyDepositEnabled) {
      this.onFormNextStep(this.ID_COPIES_STEP);
    }
  }

  save(saveAndExit: boolean) {
    const payload = this.buildPayload();
    this.saveEvent.emit({ ...payload, step: String(this.currentStep), saveAndExit });
    if (saveAndExit) {
      this.dialogRef.close();
    }
  }

  private buildPayload(): any {
    const result: any = {};
    const previousData = this.data?.LAFTData || {};

    for (const [key, value] of Object.entries(this.formData || {})) {
      if (value !== null && value !== '' && value !== undefined && !(Array.isArray(value) && value.length === 0)) {
        result[key] = value;
      }
    }

    [
      'idCertificatePicture',
      'beneficiaryDocument',
      'beneficiaryPersonDocument',
      'idFrontPicture',
      'idBackPicture',
    ].forEach(field => {
      if (result[field] !== undefined && previousData[field] !== undefined && result[field] === previousData[field]) {
        delete result[field];
      }
    });

    if ((this.formData?.separateProperty === true && !result.idCertificatePicture) || this.formData?.separateProperty === false) {
      delete result.idCertificatePicture;
    }

    return result;
  }

  close(): void {
    this.removeUrlStep();
    this.dialogRef.close();
  }

  setFormData(data: any) {
    const getValue = (newValue: any, oldValue: any) => {
      if (data.hasOwnProperty(newValue)) {
        const value = data[newValue];
        if (Array.isArray(value) && value.length === 0) {
          return [];
        }
        if (value === '' || value === null) {
          return value;
        }
        return data[newValue];
      }
      return oldValue;
    };

    const phone = data?.cellphone;
    this.formData = {
      names: getValue('names', this.formData?.names),
      familyNames:
        getValue('familyNames', this.formData?.familyNames) || getValue('lastNames', this.formData?.familyNames),
      email: getValue('email', this.formData?.email),
      idType: getValue('idType', this.formData?.idType),
      idNumber: getValue('idNumber', this.formData?.idNumber),
      phone: getValue('phone', this.formData?.phone),
      phoneCode: getValue('phoneCode', this.formData?.phoneCode),
      phoneCountry: getValue('phoneCountry', this.formData?.phoneCountry),
      educationLevel: getValue('educationLevel', this.formData?.educationLevel),
      nationality: getValue('nationality', this.formData?.nationality),
      occupation: getValue('occupation', this.formData?.occupation),
      economicActivityType: getValue('economicActivityType', this.formData?.economicActivityType),
      mainEconomicActivity: getValue('mainEconomicActivity', this.formData?.mainEconomicActivity),
      annualIncome: getValue('annualIncome', this.formData?.annualIncome),
      isPEP: getValue('isPEP', this.formData?.isPEP),
      hasPEPRelatives: getValue('hasPEPRelatives', this.formData?.hasPEPRelatives),
      isBeneficiary: getValue('isBeneficiary', this.formData?.isBeneficiary),
      fundsOrigin: getValue('fundsOrigin', this.formData?.fundsOrigin),
      fundsOriginOtherOption: getValue('fundsOriginOtherOption', this.formData?.fundsOriginOtherOption),
      fundsOriginLocation: getValue('fundsOriginLocation', this.formData?.fundsOriginLocation),
      fundsOriginLocationOtherOption: getValue(
        'fundsOriginLocationOtherOption',
        this.formData?.fundsOriginLocationOtherOption
      ),
      birthDate: getValue('birthDate', this.formData?.birthDate),
      maritalStatus: getValue('maritalStatus', this.formData?.maritalStatus),
      partnerIdType: getValue('partnerIdType', this.formData?.partnerIdType),
      partnerNames: getValue('partnerNames', this.formData?.partnerNames),
      separateProperty: getValue('separateProperty', this.formData?.separateProperty),
      partnerLastNames: getValue('partnerLastNames', this.formData?.partnerLastNames),
      idCertificatePicture: data.hasOwnProperty('idCertificatePicture')
        ? data.idCertificatePicture
        : this.formData?.idCertificatePicture ?? '',
      idFrontPicture: data.hasOwnProperty('idFrontPicture')
        ? data.idFrontPicture
        : this.formData?.idFrontPicture ?? '',
      idBackPicture: data.hasOwnProperty('idBackPicture')
        ? data.idBackPicture
        : this.formData?.idBackPicture ?? '',

      mainEconomicActivityOther: getValue('mainEconomicActivityOther', this.formData?.mainEconomicActivityOther),
      partnerIdNumber: getValue('partnerIdNumber', this.formData?.partnerIdNumber),
      department: getValue('department', this.formData?.department),
      province: getValue('province', this.formData?.province),
      district: getValue('district', this.formData?.district),
      address: getValue('address', this.formData?.address),
      isInvestigated: getValue('isInvestigated', this.formData?.isInvestigated),
      hasBeenWorkingOnPublicInstitution: getValue(
        'hasBeenWorkingOnPublicInstitution',
        this.formData?.hasBeenWorkingOnPublicInstitution
      ),
      positionPartner: getValue('positionPartner', this.formData?.positionPartner),
      institutionName: getValue('institutionName', this.formData?.institutionName),
      partnerName: getValue('partnerName', this.formData?.partnerName),
      familyMembers: getValue('familyMembers', this.formData?.familyMembers),
      family2ndDegreeMembers: getValue('family2ndDegreeMembers', this.formData?.family2ndDegreeMembers),
      acceptsContract: getValue('acceptsContract', this.formData?.acceptsContract),
      step: this.currentStep.toString(),
      prosecutorOffice: getValue('prosecutorOffice', this.formData?.prosecutorOffice),
      caseStatus: getValue('caseStatus', this.formData?.caseStatus),
      caseNumber: getValue('caseNumber', this.formData?.caseNumber),
      defendantName: getValue('defendantName', this.formData?.defendantName),
      isBeneficiaryPersonPep: getValue('isBeneficiaryPersonPep', this.formData?.isBeneficiaryPersonPep),
      beneficiaryPersonCharge: getValue('beneficiaryPersonCharge', this.formData?.beneficiaryPersonCharge),
      beneficiaryPersonInstitution: getValue(
        'beneficiaryPersonInstitution',
        this.formData?.beneficiaryPersonInstitution
      ),
      beneficiaryPersonName: getValue('beneficiaryPersonName', this.formData?.beneficiaryPersonName),
      beneficiaryDocument: getValue('beneficiaryDocument', this.formData?.beneficiaryDocument),
      beneficiaryPersonDocument: getValue('beneficiaryPersonDocument', this.formData?.beneficiaryPersonDocument),
      beneficiaryRepresentativeType: getValue(
        'beneficiaryRepresentativeType',
        this.formData?.beneficiaryRepresentativeType
      ),
      beneficiaryBusinessName: getValue('beneficiaryBusinessName', this.formData?.beneficiaryBusinessName),
      beneficiaryRuc: getValue('beneficiaryRuc', this.formData?.beneficiaryRuc),
      beneficiaryPowerType: getValue('beneficiaryPowerType', this.formData?.beneficiaryPowerType),
      beneficiaryPersonDocumentNumber: getValue(
        'beneficiaryPersonDocumentNumber',
        this.formData?.beneficiaryPersonDocumentNumber
      ),
      beneficiaryPersonDocumentType: getValue(
        'beneficiaryPersonDocumentType',
        this.formData?.beneficiaryPersonDocumentType
      ),
      beneficiaryPersonPowerType: getValue('beneficiaryPersonPowerType', this.formData?.beneficiaryPersonPowerType),
      country: getValue('country', this.formData?.country),
      acceptsCommercialUse: getValue('acceptsCommercialUse', this.formData?.acceptsCommercialUse),
      estimatedPatrimony: getValue('estimatedPatrimony', this.formData?.estimatedPatrimony),
    };

  }
}
